/**
 * Shell-owned UI state that screens and chrome share: the content width
 * mode a screen asks for, and whether the matter search is open (the top
 * bar trigger, the bottom bar and the keyboard shortcuts all open the same
 * palette).
 */

import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react';

export type WidthMode = 'wide' | 'narrow' | 'full';

type ShellState = {
  width: WidthMode;
  setWidth: (w: WidthMode) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
};

const ShellContext = createContext<ShellState | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [width, setWidth] = useState<WidthMode>('wide');
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const value = useMemo(() => ({ width, setWidth, searchOpen, setSearchOpen, menuOpen, setMenuOpen }), [width, searchOpen, menuOpen]);
  return <ShellContext value={value}>{children}</ShellContext>;
}

export function useShell(): ShellState {
  const ctx = use(ShellContext);
  if (!ctx) throw new Error('useShell outside ShellProvider');
  return ctx;
}

/** A screen declares its width; the shell resets to wide when it unmounts. */
export function useShellWidth(mode: WidthMode): void {
  const { setWidth } = useShell();
  useEffect(() => {
    setWidth(mode);
    return () => setWidth('wide');
  }, [mode, setWidth]);
}

/** Border-box widths: the 24 px gutters sit inside, so wide gives 1400 px of content (DESIGN.md). */
export const WIDTH_CLASS: Record<WidthMode, string> = {
  wide: 'max-w-[1448px]',
  narrow: 'max-w-[908px]',
  full: 'max-w-none',
};
